import {
  Contrasts,
  Divider,
  Grid,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { AssetAvailabilityReason } from '@see-power-pft-common-js/availability/asset-availability/enums/asset-availability-reason';
import { SiteAvailabilityReason } from '@see-power-pft-common-js/availability/site-availability/enums/site-availability-reason';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { UK_TIMEZONE } from '@see-power-pft-common-js/utils/product-mapper';
import { ShellModal } from 'components/Modal/ShellModal';
import { formatInTimeZone } from 'date-fns-tz';
import { usePlutoAvailabilityApiQuery } from 'hooks/useQuery/plutoAvailabilityApi';
import {
  assetAvailabilityReasonDetails,
  siteAvailabilityReasonDetails,
} from 'pages/Availability/common/FormDrawer/constants';
import {
  GET_ASSET_AVAILABILITY_AUDITS,
  GET_SITE_AVAILABILITY_AUDITS,
} from 'pages/Availability/queries';
import {
  AssetAvailabilityAudit,
  SiteAvailabilityAudit,
} from 'pages/Availability/types';
import { JSX, useEffect } from 'react';
import { Timezone } from 'types/timezone';

const dateFormat = "dd MMM yy 'at' HH:mm:ss";

interface ReductionInfoModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  availabilityId: number;
  availabilityControl: SiteAvailabilityControl;
  timezone: Timezone;
}

type SiteUpdatedValue = SiteAvailabilityAudit['updatedValue'];
type AssetUpdatedValue = AssetAvailabilityAudit['updatedValue'];
type UpdatedValue = SiteUpdatedValue | AssetUpdatedValue;

function getChanges(
  current: UpdatedValue,
  previous: UpdatedValue,
  isAssetLevel: boolean,
): JSX.Element[] {
  const reasonDetails = isAssetLevel
    ? assetAvailabilityReasonDetails
    : siteAvailabilityReasonDetails;

  const reasonKey = isAssetLevel
    ? 'assetAvailabilityReason'
    : 'siteAvailabilityReason';

  const fieldMap: {
    key: string;
    label: string;
    format?: (val: any) => string;
  }[] = [
    {
      key: 'start',
      label: 'Start',
      format: (val: Date | string) =>
        formatInTimeZone(val, UK_TIMEZONE, dateFormat),
    },
    {
      key: 'end',
      label: 'End',
      format: (val: Date | string | null) =>
        val ? formatInTimeZone(val, UK_TIMEZONE, dateFormat) : 'None',
    },
    { key: 'maxImportPower', label: 'Max Import Power' },
    { key: 'maxExportPower', label: 'Max Export Power' },
    { key: 'energyCapacity', label: 'Energy Capacity' },
    { key: 'notes', label: 'Notes' },
    {
      key: reasonKey,
      label: 'Reason',
      format: (val: SiteAvailabilityReason | AssetAvailabilityReason | null) =>
        reasonDetails.find((r) => r.id === val)?.title ?? 'None',
    },
  ];

  return fieldMap.reduce<JSX.Element[]>((acc, { key, label, format: fmt }) => {
    const prev = (previous as any)[key];
    const curr = (current as any)[key];

    const areDates = prev instanceof Date && curr instanceof Date;
    const areEqual = areDates
      ? prev.getTime() === curr.getTime()
      : prev === curr;

    if (!areEqual) {
      const prevText = fmt ? fmt(prev) : String(prev ?? 'None');
      const currText = fmt ? fmt(curr) : String(curr ?? 'None');

      acc.push(
        <div key={key}>
          Edited {label} from {prevText} to <b>{currText}</b>
        </div>,
      );
    }

    return acc;
  }, []);
}

export const ReductionInfoModal: React.FC<ReductionInfoModalProps> = ({
  isOpen,
  setIsOpen,
  availabilityId,
  availabilityControl,
  timezone,
}) => {
  const isAssetLevel =
    availabilityControl === SiteAvailabilityControl.AssetLevel;

  // Site availability audits query
  const { data: siteData, refetch: refetchSite } =
    usePlutoAvailabilityApiQuery<{
      siteAvailabilityAudits: SiteAvailabilityAudit[];
    }>(GET_SITE_AVAILABILITY_AUDITS, {
      skip: availabilityId === null || !isOpen || isAssetLevel,
      variables: {
        siteAvailabilityId: availabilityId,
      },
    });

  // Asset availability audits query
  const { data: assetData, refetch: refetchAsset } =
    usePlutoAvailabilityApiQuery<{
      assetAvailabilityAudits: AssetAvailabilityAudit[];
    }>(GET_ASSET_AVAILABILITY_AUDITS, {
      skip: availabilityId === null || !isOpen || !isAssetLevel,
      variables: {
        assetAvailabilityId: availabilityId,
      },
    });

  useEffect(() => {
    if (isOpen && availabilityId !== null) {
      if (isAssetLevel) {
        refetchAsset();
      } else {
        refetchSite();
      }
    }
  }, [isOpen, availabilityId, isAssetLevel, refetchSite, refetchAsset]);

  const auditData = isAssetLevel
    ? (assetData?.assetAvailabilityAudits ?? [])
    : (siteData?.siteAvailabilityAudits ?? []);

  const audits = [...auditData].sort(
    (a, b) =>
      new Date(b.updatedValue.updatedByUserAt).getTime() -
      new Date(a.updatedValue.updatedByUserAt).getTime(),
  );

  return (
    <ShellModal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title="Reduction Info"
      actions={[
        {
          label: 'Close',
          action: () => setIsOpen(false),
        },
      ]}
      showFooter={true}
      loading={false}
      size={Sizes.Medium}
      minWidth={800}
      zIndex={100}
    >
      <Grid rowGap="30px" columnGap="20px" width="100%" columns="30% 70%">
        {audits && audits.length > 0 ? (
          audits.map((audit, index) => {
            const dateText = formatInTimeZone(
              audit.updatedValue.updatedByUserAt,
              timezone,
              dateFormat,
            );
            return (
              <>
                <Grid.Cell key={`meta-${audit.updatedAt}-${index}-date`}>
                  {index !== 0 && (
                    <Divider
                      contrast={Contrasts.Subtle}
                      style={{
                        marginBottom: '30px',
                        marginRight: '-10%',
                        width: '108%',
                      }}
                    />
                  )}
                  <Text size={Sizes.Medium} style={{ color: 'gray' }}>
                    {dateText}
                    <br />
                    By {audit.updatedByUserEmail}
                  </Text>
                </Grid.Cell>

                <Grid.Cell key={`meta-${audit.updatedAt}-${index}`}>
                  {index !== 0 && (
                    <Divider
                      contrast={Contrasts.Subtle}
                      style={{ marginBottom: '30px', width: '110%' }}
                    />
                  )}
                  <Text type="span" size={Sizes.Medium}>
                    {audit.action === 'INSERT' && (
                      <>
                        Created reduction
                        {audit.updatedValue.notes && (
                          <>
                            <br />
                            Notes: {audit.updatedValue.notes}
                          </>
                        )}
                      </>
                    )}
                    {(audit.action === 'DELETE' ||
                      audit.updatedValue.isCancelled) &&
                      'Cancelled reduction'}

                    {audit.action === 'UPDATE' && audits[index + 1]
                      ? getChanges(
                          audit.updatedValue,
                          audits[index + 1].updatedValue,
                          isAssetLevel,
                        )
                      : audit.action === 'UPDATE' && 'Updated reduction'}
                  </Text>
                </Grid.Cell>
              </>
            );
          })
        ) : (
          <Grid.Cell>
            <Text type="p" size={Sizes.Medium}>
              No reduction information available.
            </Text>
          </Grid.Cell>
        )}
      </Grid>
    </ShellModal>
  );
};
